import { useState, useEffect } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { adminGetBlockedIPs, adminUnblockIP, adminGetLoginAttempts } from '../../lib/adminApi';

const BLUE = '#0176CE';
const RED  = '#dc2626';

function fmtTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtUntil(d) {
  if (!d) return '—';
  const diff = new Date(d) - new Date();
  if (diff <= 0) return 'expired';
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `${mins}m remaining`;
  return `${Math.round(mins/60)}h remaining`;
}

export default function AdminSecurity() {
  const [blockedIPs,  setBlockedIPs]  = useState([]);
  const [attempts,    setAttempts]    = useState([]);
  const [hours,       setHours]       = useState(24);
  const [loading,     setLoading]     = useState(true);
  const [unblocking,  setUnblocking]  = useState(null);
  const [tab,         setTab]         = useState('blocked');

  async function load() {
    setLoading(true);
    try {
      const [bRes, aRes] = await Promise.all([
        adminGetBlockedIPs(),
        adminGetLoginAttempts(hours),
      ]);
      setBlockedIPs(bRes.blocked_ips || []);
      setAttempts(aRes.attempts || []);
    } catch (_) {}
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [hours]);

  async function handleUnblock(id) {
    setUnblocking(id);
    try {
      await adminUnblockIP(id);
      setBlockedIPs(prev => prev.map(b => b.block_id === id ? { ...b, unblocked_at: new Date().toISOString() } : b));
    } catch (_) {}
    finally { setUnblocking(null); }
  }

  const activeBlocks   = blockedIPs.filter(b => !b.unblocked_at);
  const resolvedBlocks = blockedIPs.filter(b => b.unblocked_at);
  const failedAttempts = attempts.filter(a => !a.success);
  const successAttempts= attempts.filter(a => a.success);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
      <AdminSidebar active="security" />
      <main style={{ flex: 1, padding: '36px 40px', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0a2540', margin: 0 }}>Security</h1>
            <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Blocked IPs and login attempt monitoring</p>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>Attempts window:</span>
            {[6, 24, 48, 168].map(h => (
              <button key={h} onClick={() => setHours(h)}
                style={{ background: hours === h ? BLUE : '#fff', color: hours === h ? '#fff' : '#374151', border: '1px solid #e2e8f0', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {h < 24 ? `${h}h` : `${h/24}d`}
              </button>
            ))}
            <button onClick={load} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 7, padding: '6px 12px', fontSize: 12, color: '#374151', cursor: 'pointer', marginLeft: 8 }}>Refresh</button>
          </div>
        </div>

        {/* Summary cards */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 28 }}>
          {[
            { label: 'Active Blocks',   value: activeBlocks.length,    color: activeBlocks.length > 0 ? RED : '#10b981' },
            { label: 'Failed Attempts', value: failedAttempts.length,  color: failedAttempts.length > 10 ? RED : '#f59e0b' },
            { label: 'Successful Logins', value: successAttempts.length, color: '#10b981' },
            { label: 'Unique Blocked IPs', value: new Set(activeBlocks.map(b=>b.ip_address)).size, color: activeBlocks.length > 0 ? RED : '#10b981' },
          ].map(c => (
            <div key={c.label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px', flex: 1 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: c.color }}>{c.value}</div>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 4 }}>{c.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: 20 }}>
          {[
            { key: 'blocked', label: `Blocked IPs${activeBlocks.length ? ` (${activeBlocks.length})` : ''}` },
            { key: 'attempts', label: `Login Attempts (${hours < 24 ? hours+'h' : (hours/24)+'d'})` },
            { key: 'resolved', label: `Resolved Blocks${resolvedBlocks.length ? ` (${resolvedBlocks.length})` : ''}` },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ background: 'none', border: 'none', padding: '8px 16px', fontSize: 13, fontWeight: tab === t.key ? 700 : 500,
                color: tab === t.key ? BLUE : '#6b7280', cursor: 'pointer',
                borderBottom: tab === t.key ? `2px solid ${BLUE}` : '2px solid transparent', marginBottom: -2 }}>
              {t.label}
            </button>
          ))}
        </div>

        {loading && <div style={{ color: '#9ca3af', padding: '40px', textAlign: 'center' }}>Loading…</div>}

        {/* Active blocked IPs */}
        {!loading && tab === 'blocked' && (
          activeBlocks.length === 0
            ? <div style={{ color: '#9ca3af', padding: '40px', textAlign: 'center' }}>No active IP blocks.</div>
            : (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['IP Address', 'Blocked At', 'Expires', 'Reason', 'Recent Fails', 'Emails Tried', ''].map(h => (
                        <th key={h} style={{ padding: '10px 16px', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activeBlocks.map(b => (
                      <tr key={b.block_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 600, fontFamily: 'monospace', color: RED }}>{b.ip_address}</td>
                        <td style={{ padding: '10px 16px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtTime(b.blocked_at)}</td>
                        <td style={{ padding: '10px 16px', fontSize: 12, color: '#f59e0b', whiteSpace: 'nowrap' }}>{fmtUntil(b.blocked_until)}</td>
                        <td style={{ padding: '10px 16px', fontSize: 12, color: '#374151' }}>{b.reason || '—'}</td>
                        <td style={{ padding: '10px 16px', fontSize: 12, color: b.recent_failures > 0 ? RED : '#374151', fontWeight: b.recent_failures > 0 ? 700 : 400 }}>{b.recent_failures}</td>
                        <td style={{ padding: '10px 16px', fontSize: 11, color: '#6b7280', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.emails_attempted || '—'}</td>
                        <td style={{ padding: '10px 16px' }}>
                          <button
                            onClick={() => handleUnblock(b.block_id)}
                            disabled={unblocking === b.block_id}
                            style={{ background: 'none', border: `1px solid ${BLUE}`, borderRadius: 6, color: BLUE, fontSize: 12, padding: '4px 12px', cursor: 'pointer', fontWeight: 600 }}>
                            {unblocking === b.block_id ? 'Unblocking…' : 'Unblock'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
        )}

        {/* Login attempts feed */}
        {!loading && tab === 'attempts' && (
          attempts.length === 0
            ? <div style={{ color: '#9ca3af', padding: '40px', textAlign: 'center' }}>No login attempts found.</div>
            : (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Time', 'IP Address', 'Email', 'Result', 'Reason'].map(h => (
                        <th key={h} style={{ padding: '10px 16px', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {attempts.map(a => (
                      <tr key={a.attempt_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 16px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtTime(a.created_at)}</td>
                        <td style={{ padding: '10px 16px', fontSize: 12, fontFamily: 'monospace', color: a.success ? '#374151' : RED }}>{a.ip_address}</td>
                        <td style={{ padding: '10px 16px', fontSize: 12, color: '#374151' }}>{a.email || '—'}</td>
                        <td style={{ padding: '10px 16px' }}>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                            background: a.success ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                            color: a.success ? '#10b981' : RED }}>
                            {a.success ? '✓ Success' : '✗ Failed'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 16px', fontSize: 12, color: '#6b7280' }}>{a.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
        )}

        {/* Resolved blocks */}
        {!loading && tab === 'resolved' && (
          resolvedBlocks.length === 0
            ? <div style={{ color: '#9ca3af', padding: '40px', textAlign: 'center' }}>No resolved blocks.</div>
            : (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['IP Address', 'Blocked At', 'Unblocked At', 'Reason'].map(h => (
                        <th key={h} style={{ padding: '10px 16px', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {resolvedBlocks.map(b => (
                      <tr key={b.block_id} style={{ borderBottom: '1px solid #f1f5f9', opacity: 0.7 }}>
                        <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 600, fontFamily: 'monospace' }}>{b.ip_address}</td>
                        <td style={{ padding: '10px 16px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtTime(b.blocked_at)}</td>
                        <td style={{ padding: '10px 16px', fontSize: 12, color: '#10b981', whiteSpace: 'nowrap' }}>{fmtTime(b.unblocked_at)}</td>
                        <td style={{ padding: '10px 16px', fontSize: 12, color: '#374151' }}>{b.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
        )}

      </main>
    </div>
  );
}
